import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import { handleUpdateTimestamp } from 'src/ui/wards/common/util/dateUtils';

import DropdownSelect, { OptionProps } from 'src/ui/wards/common/select/DropdownSelect';
import Button from 'src/ui/wards/common/button/Button';
import {
  disasterColumns,
  disasterCustomStyles,
  transformToDisasterRows,
} from '../DisasterRegionColumns';
import {
  BranchSituation,
  branchSummaryColumns,
  branchSummaryCustomStyles,
} from './BranchSummaryColumns';
import { BranchTableHeader } from './BranchTableHeader';
import { FormRow } from 'src/ui/wards/common/form/FormRow';

export const BranchSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    branchOptions,
    workplaceFilterParameters,
    patchWorkplaceFilterParameters,
    setCustomerBranchCode,
  } = useWaFilterStore();
  const { selectedDisaster } = useWaDisasterStore();
  const { fetchSummaryByBranch, fetchDisasterAreaBranches, fetchBranchOptions } =
    useWaWorkplaceService();

  const [tableData, setTableData] = useState<BranchSituation[]>([]);
  const [allBranchesSummary, setAllBranchesSummary] = useState<string[]>([]);
  const [lastClickedTime, setLastClickedTime] = useState<string | null>(() =>
    localStorage.getItem('last_branch_click_time')
  );

  const zigzagData = useMemo(() => transformToDisasterRows(branchOptions), [branchOptions]);

  const branchDropdownOptions = useMemo(
    (): OptionProps[] => [
      { title: t('wards.desktop.summary.all'), value: '' },
      ...branchOptions.map((branch) => ({
        title: `${branch.code} ${branch.name}`,
        value: branch.code,
      })),
    ],
    [branchOptions, t]
  );

  const selectedBranchOption = useMemo(
    () =>
      branchDropdownOptions.find((opt) => opt.value === workplaceFilterParameters.branchCode) ||
      branchDropdownOptions[0],
    [branchDropdownOptions, workplaceFilterParameters.branchCode]
  );

  const handleFetchData = useCallback(async () => {
    if (!selectedDisaster?.id) return;

    try {
      const rawData = await fetchSummaryByBranch(
        selectedDisaster.id,
        workplaceFilterParameters.branchCode
      );
      setTableData(rawData || []);
    } catch (error) {
      console.error('Error while loading branch summary:', error);
      setTableData([]);
    }
  }, [fetchSummaryByBranch, selectedDisaster?.id, workplaceFilterParameters.branchCode]);

  const handleBusinessLocationSelected = useCallback(
    (branchCode: string, salesOfficeCode: string) => {
      patchWorkplaceFilterParameters({
        branchCode,
        salesOfficeCode,
        customerId: undefined,
      });
      setCustomerBranchCode(branchCode);
      wardsNavigate(navigate, WaScreensMap[WA_Screen.BusinessSummary].path);
    },
    [navigate, patchWorkplaceFilterParameters, setCustomerBranchCode]
  );

  const handleRegionalOfficeChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const branchCode = event.target.value;

    patchWorkplaceFilterParameters({
      branchCode,
      salesOfficeCode: '',
      sectionCode: '',
      squadCode: '',
      customerId: undefined,
    });
    setCustomerBranchCode(branchCode);
  };

  const handleUpdateClick = () => {
    handleUpdateTimestamp('last_branch_click_time', setLastClickedTime);
    handleFetchData();
  };

  useEffect(() => {
    fetchBranchOptions();
  }, [fetchBranchOptions]);

  useEffect(() => {
    handleUpdateTimestamp('last_branch_click_time', setLastClickedTime);
  }, []);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, selectedDisaster?.id]);

  useEffect(() => {
    const fetchHighlighting = async () => {
      if (!selectedDisaster?.id) return;
      try {
        const codes = await fetchDisasterAreaBranches(selectedDisaster.id);
        setAllBranchesSummary(codes || []);
      } catch (e) {
        console.error('Highlighting fetch failed', e);
      }
    };
    fetchHighlighting();
  }, [fetchDisasterAreaBranches, selectedDisaster?.id]);

  return (
    <div className="mx-auto flex max-w-fit flex-col gap-2 pb-4">
      <div className="space-y-1 text-2xl font-bold">{t('wards.desktop.summary.title')}</div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col items-center gap-4 rounded-sm border-2 border-sky-600 p-3 shadow-sm sm:flex-row">
          <div className="w-full flex-1">
            <FormRow title={t('wards.common.branchOffice')} required>
              <DropdownSelect
                selectedValue={selectedBranchOption}
                optionValues={branchDropdownOptions}
                onChange={handleRegionalOfficeChanged}
              />
            </FormRow>
          </div>
          <Button title={t('wards.common.update')} onClick={handleUpdateClick} />
          {lastClickedTime && (
            <span className="self-center text-sm text-gray-600">{lastClickedTime}</span>
          )}
        </div>
        <div className="w-full rounded-sm border-2 border-sky-600 bg-gray-50 p-3 text-center text-sm">
          {t('wards.common.displayedContent')}
        </div>
      </div>

      <div className="flex h-fit flex-col overflow-hidden rounded-sm border border-gray-300 shadow-sm">
        <DataTable
          noDataComponent={<div className="p-4">{t('wards.common.noResults')}</div>}
          noTableHead
          columns={disasterColumns(allBranchesSummary)}
          data={zigzagData}
          customStyles={disasterCustomStyles}
          dense
        />
      </div>

      <div className="flex w-full flex-col">
        <h2 className="mb-2 text-sm font-semibold tracking-wide">
          {t('wards.desktop.summary.branch.title')}
        </h2>

        <div className="w-full overflow-x-auto rounded-sm border border-gray-300 shadow-sm">
          <div className="inline-block min-w-max align-middle">
            <BranchTableHeader t={t} />
            <DataTable
              noDataComponent={<div className="p-4">{t('wards.common.noResults')}</div>}
              noTableHead
              columns={branchSummaryColumns(handleBusinessLocationSelected)}
              data={tableData}
              customStyles={branchSummaryCustomStyles}
              dense
              pagination
              paginationPerPage={10}
              conditionalRowStyles={[totalRowStyle(workplaceFilterParameters.branchCode)]}
            />
          </div>
        </div>
      </div>

      <Button title={t('wards.common.back')} onClick={() => navigate(-1)} className="self-start" />
    </div>
  );
};

const totalRowStyle = (branchCode: string) => ({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  when: ((_row: BranchSituation, index: number) => index === 0 && !branchCode) as any,
  style: {
    backgroundColor: '#f3f4f6',
    fontWeight: 'bold' as const,
    color: '#111827',
  },
});
